import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../../contexts/ToastContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';

/**
 * Modal for confirming user deactivation
 */
const UserDeactivationModal = ({ isOpen, user, onClose, onDeactivated }) => {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [sendNotification, setSendNotification] = useState(true);

  /**
   * Handle deactivation confirmation
   */
  const handleDeactivate = async () => {
    if (!reason.trim()) {
      showToast('Please provide a reason for deactivation', 'error');
      return;
    }

    try {
      setLoading(true);
      const token = await getToken();
      
      const response = await apiRequest(`/api/admin/users/${user.id}/deactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: reason.trim(),
          send_notification: sendNotification
        })
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const result = await response.json();
      
      showToast('User deactivated successfully', 'success');
      onDeactivated(result);
      handleClose();

    } catch (error) {
      console.error('Error deactivating user:', error);
      showToast(error.message || 'Failed to deactivate user', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reset form and close modal
   */
  const handleClose = () => {
    setReason('');
    setSendNotification(true);
    onClose();
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center mb-4">
          <div className="w-10 h-10 bg-red-900/30 rounded-full flex items-center justify-center mr-3">
            <span className="text-red-400 text-lg">⚠️</span>
          </div>
          <div>
            <h3 className="text-lg font-medium text-white dark:text-white">Deactivate User</h3>
            <p className="text-sm text-gray-400">
              {user.first_name} {user.last_name} ({user.email})
            </p>
          </div>
        </div>

        {/* Warning Message */}
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 mb-4">
          <p className="text-red-300 text-sm">
            <strong>Warning:</strong> This action will immediately disable the user's account and prevent them from accessing the platform. All their data will be preserved but they will not be able to log in or use any services.
          </p>
        </div>

        {/* Form */}
        <div className="space-y-4">
          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2 dark:text-gray-600">
              Reason for deactivation *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter the reason for deactivating this user account..."
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-red-500 resize-none dark:text-white"
              rows={3}
              maxLength={500}
            />
            <div className="text-xs text-gray-400 mt-1">
              {reason.length}/500 characters
            </div>
          </div>

          {/* Notification Option */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="send_notification"
              checked={sendNotification}
              onChange={(e) => setSendNotification(e.target.checked)}
              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-600 rounded bg-gray-700"
            />
            <label htmlFor="send_notification" className="ml-2 text-sm text-gray-300 dark:text-gray-600">
              Send email notification to user about account deactivation
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors disabled:opacity-50 dark:text-white"
          >
            Cancel
          </button>
          <button
            onClick={handleDeactivate}
            disabled={loading || !reason.trim()}
            className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed dark:text-white"
          >
            {loading ? 'Deactivating...' : 'Deactivate User'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDeactivationModal;
